import { useEffect, useState } from 'react';
import { DEFAULT_TIMEZONE, TIMEZONE_VALUES } from './timezones.js';

// Short timezone abbreviation map: [standardLabel, daylightLabel]. Second
// value equals first for zones with no DST variant. Browsers disagree on
// what `Intl.DateTimeFormat(..., { timeZoneName: 'short' })` emits for
// continental-European zones (Chrome gives "GMT+1" instead of "CET"), so we
// keep our own map rather than trusting Intl for the suffix. DST decision
// still uses Intl-derived offsets, see isDst() below.
const TZ_LABELS = {
  'UTC':                 ['UTC', 'UTC'],
  'Europe/London':       ['GMT', 'BST'],
  'Europe/Paris':        ['CET', 'CEST'],
  'Europe/Berlin':       ['CET', 'CEST'],
  'Europe/Kyiv':         ['EET', 'EEST'],
  'Europe/Moscow':       ['MSK', 'MSK'],
  'Europe/Istanbul':     ['TRT', 'TRT'],
  'Europe/Athens':       ['EET', 'EEST'],
  'Europe/Bucharest':    ['EET', 'EEST'],
  'Europe/Warsaw':       ['CET', 'CEST'],
  'Europe/Helsinki':     ['EET', 'EEST'],
  'Asia/Tbilisi':        ['GET', 'GET'],
  'Asia/Baku':           ['AZT', 'AZT'],
  'Asia/Dubai':          ['GST', 'GST'],
  'Asia/Kolkata':        ['IST', 'IST'],
  'Asia/Shanghai':       ['CST', 'CST'],
  'Asia/Tokyo':          ['JST', 'JST'],
  'America/New_York':    ['EST', 'EDT'],
  'America/Chicago':     ['CST', 'CDT'],
  'America/Denver':      ['MST', 'MDT'],
  'America/Los_Angeles': ['PST', 'PDT']
};

function safeTz(tz) {
  return tz && TIMEZONE_VALUES.has(tz) ? tz : DEFAULT_TIMEZONE;
}

// Compute the UTC offset (in minutes) of a given IANA zone at a given instant.
// Works by asking Intl to render the instant as wall-clock parts in that zone,
// then treating those parts as if they were UTC and diffing against the real
// UTC timestamp. Positive = east of UTC.
function offsetMinutes(tz, date) {
  const dtf = new Intl.DateTimeFormat('en-GB', {
    timeZone: tz,
    year:   'numeric',
    month:  '2-digit',
    day:    '2-digit',
    hour:   '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23'
  });
  const parts = {};
  for (const p of dtf.formatToParts(date)) parts[p.type] = p.value;
  const asUtc = Date.UTC(
    +parts.year, +parts.month - 1, +parts.day,
    +parts.hour, +parts.minute, +parts.second
  );
  return (asUtc - date.getTime()) / 60000;
}

// A zone is in DST at `date` when its offset differs from its winter-standard
// offset. For every supported zone, January 15 is unambiguously on the
// standard (non-DST) side of the calendar. Zones without DST collapse to a
// constant offset so this always returns false for them.
function isDst(tz, date) {
  const labels = TZ_LABELS[tz];
  if (!labels || labels[0] === labels[1]) return false;
  const y = date.getUTCFullYear();
  const standardProbe = new Date(Date.UTC(y, 0, 15)); // mid-January
  const stdOff = offsetMinutes(tz, standardProbe);
  return offsetMinutes(tz, date) !== stdOff;
}

export function tzSuffix(tz = DEFAULT_TIMEZONE, date = new Date()) {
  const t = safeTz(tz);
  const labels = TZ_LABELS[t] ?? [t, t];
  return isDst(t, date) ? labels[1] : labels[0];
}

// Build "21 Apr 2026, 03:00:00" in the given zone, without the suffix.
function formatBody(date, tz) {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: tz,
    day:    '2-digit',
    month:  'short',
    year:   'numeric',
    hour:   '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23'
  }).format(date);
}

// Canonical UI timestamp: "21 Apr 2026, 03:00:00 UTC" / "… EEST" / "… MSK".
// Accepts a Date, a numeric epoch-ms, or a parseable string. Returns an
// em-dash for anything that resolves to an invalid Date so callers don't
// need to guard null/undefined separately.
export function formatInTz(input, tz = DEFAULT_TIMEZONE) {
  const d = input instanceof Date ? input : new Date(input ?? NaN);
  if (Number.isNaN(d.getTime())) return '—';
  const t = safeTz(tz);
  return `${formatBody(d, t)} ${tzSuffix(t, d)}`;
}

// Same output split into body + suffix so the Sidebar pill can style the
// suffix separately from the main clock text.
export function formatInTzParts(input, tz = DEFAULT_TIMEZONE) {
  const d = input instanceof Date ? input : new Date(input ?? NaN);
  if (Number.isNaN(d.getTime())) return { body: '—', tz: '' };
  const t = safeTz(tz);
  return { body: formatBody(d, t), tz: tzSuffix(t, d) };
}

// One-second-tick React hook for the live clock in the sidebar. Timezone
// lives entirely in the formatter — this hook just surfaces a fresh Date.
export function useClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return now;
}

// ── Back-compat shims ──────────────────────────────────────────────────────
// Legacy callsites imported `formatUtc` / `useUtcClock`. Kept as thin
// wrappers so lingering imports don't break; new code should use the
// timezone-aware variants above.
export function formatUtc(input) {
  return formatInTz(input, 'UTC');
}

export const useUtcClock = useClock;
